import UserContext from "context/UserContext";
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router";
import JSZip from "jszip";
import ApiService from "../../infrastructure/Services/ApiService";
import StorageService from "../../infrastructure/StorageService";
import Constants from "../../infrastructure/Constants";
import { Card, CardHeader, CardBody, Container, Row, Col, Button, Input } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";

export default () => {
    const [items, setItems] = useState([]);
    const [rawJson, setRawJson] = useState("[]");
    const [isImporting, setImporting] = useState(false);
    const [showUnpublished, setShowUnpublished] = useState(false);
    const [importError, setImportError] = useState("");
    const user = useContext(UserContext);
    const history = useHistory();

    const getChemicalDisplayName = (chemical) => {
        const value = chemical?.DocumentName || chemical?.documentName || chemical?.PartNumber || chemical?.partNumber || chemical?.DocumentNumber || chemical?.documentNumber || "";
        return String(value).trim();
    };

    const sortChemicals = (chemicals = []) => {
        return [...chemicals].sort((left, right) => {
            const leftName = getChemicalDisplayName(left).toLocaleLowerCase();
            const rightName = getChemicalDisplayName(right).toLocaleLowerCase();

            return leftName.localeCompare(rightName) || leftName.localeCompare(rightName);
        });
    };

    const load = async () => {
        const existing = await ApiService.fromStorage("lab-bookings/chemicals.json");
        const data = sortChemicals(Array.isArray(existing) ? existing : []);
        setItems(data);
        setRawJson(JSON.stringify(data, null, 2));
    };

    useEffect(() => {
        if (!user.isAdmin) {
            history.push("/dashboard");
            return;
        }

        load();
    }, [user, history]);

    const normalizePath = (pathValue) => (pathValue || "").replace(/\\/g, "/");

    const formatIssueDate = (value) => {
        if (!value) {
            return "-";
        }

        const numericValue = typeof value === "string" ? value.match(/\/Date\((\d+)/i)?.[1] : null;
        const dateValue = numericValue ? Number(numericValue) : value;
        const formatted = moment(dateValue);

        return formatted.isValid() ? formatted.format("DD MMM YYYY") : value;
    };

    const getChemicalIdentity = (chemical) => {
        const normalizedName = (chemical?.DocumentName || chemical?.documentName || "").trim().toLowerCase();
        const normalizedPath = normalizePath(chemical?.Path || chemical?.path);
        const normalizedReference = (chemical?.PartNumber || chemical?.partNumber || chemical?.DocumentNumber || chemical?.documentNumber || "").trim().toLowerCase();

        return `${normalizedName}::${normalizedPath}::${normalizedReference}`;
    };

    const persistChemicals = async (nextItems) => {
        const sortedItems = sortChemicals(nextItems);
        setItems(sortedItems);
        setRawJson(JSON.stringify(sortedItems, null, 2));
        StorageService.set(StorageService.keys.chemicals, sortedItems);

        try {
            await ApiService.put("chemicals", { data: sortedItems });
        } catch (error) {
            console.error(error);
            toast.error("Unable to save the chemical visibility changes.");
        }
    };

    const downloadMiniSds = (chemical) => {
        const remotePath = normalizePath(chemical?.Path || chemical?.path);
        const user = StorageService.get(StorageService.keys.user);

        if (!user?.organisationId || !user?.sasToken || !remotePath) {
            toast.error("Unable to resolve the Mini SDS download link.");
            return;
        }

        const url = `${Constants.api.storageUrl}${user.organisationId}/${remotePath}?${user.sasToken}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const uploadPdfToStorage = async (fileName, fileBlob) => {
        const sasResponse = await ApiService.get(`chemicals/upload-sas/${encodeURIComponent(fileName)}`);
        if (!sasResponse || !sasResponse.url) {
            throw new Error("Unable to obtain a write SAS URL for PDF upload.");
        }

        const response = await fetch(sasResponse.url, {
            method: "PUT",
            headers: {
                "x-ms-blob-type": "BlockBlob",
                "Content-Type": fileBlob.type || "application/pdf"
            },
            body: fileBlob,
        });

        if (!response.ok) {
            throw new Error(`Failed to upload ${fileName}`);
        }
    };

    const toggleChemicalVisibility = async (chemical) => {
        const nextItems = items.map((item) => {
            if (getChemicalIdentity(item) !== getChemicalIdentity(chemical)) {
                return item;
            }

            return {
                ...item,
                IsDeleted: !((item?.IsDeleted ?? item?.isDeleted) ?? false),
            };
        });

        await persistChemicals(nextItems);
    };

    const importZip = async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) {
            return;
        }

        setImporting(true);
        setImportError("");

        try {
            const zip = await JSZip.loadAsync(file);
            const archiveEntries = Object.keys(zip.files);
            const htmlEntry = archiveEntries.find((name) => name.toLowerCase().endsWith("index.html"));

            if (!htmlEntry) {
                throw new Error("The ZIP file is missing an HTML entry such as index.html.");
            }

            const html = await zip.files[htmlEntry].async("text");
            const match = html.match(/chemicalData\s*=\s*(\[[\s\S]*?\])\s*(?:;|<)/i);

            if (!match || !match[1]) {
                throw new Error("The HTML file does not contain a chemicalData array in the expected format.");
            }

            let parsed;
            try {
                parsed = JSON.parse(match[1]);
            } catch (parseError) {
                throw new Error("The HTML file contains invalid JSON for the chemicalData array.");
            }

            if (!Array.isArray(parsed)) {
                throw new Error("The HTML file must contain a chemicalData array, not a single object.");
            }

            const docs = archiveEntries
                .filter((name) => name.toLowerCase().startsWith("docs/") && name.toLowerCase().endsWith(".pdf"))
                .map((name) => ({
                    name,
                    blob: () => zip.files[name].async("blob")
                }));

            if (docs.length === 0) {
                throw new Error("The ZIP file is missing PDF documents in the docs/ folder.");
            }

            const validated = parsed.map((item) => ({
                ...item,
                Path: normalizePath(item.Path),
                DocumentName: (item.DocumentName || "").trim(),
                IsDeleted: false,
            }));

            const uploadedCount = { value: 0 };
            const softDeletedCount = { value: 0 };

            for (const item of validated) {
                const relativePath = normalizePath(item.Path);
                const fileName = relativePath.replace(/^docs\//i, "");
                const fileEntry = docs.find((entry) => entry.name.toLowerCase().endsWith(`/${fileName.toLowerCase()}`));

                if (!fileName || !fileEntry) {
                    item.IsDeleted = true;
                    softDeletedCount.value += 1;
                    continue;
                }

                const blob = await fileEntry.blob();
                await uploadPdfToStorage(fileName, blob);
                uploadedCount.value += 1;
            }

            const result = await ApiService.post("chemicals/import", { data: validated });
            if (result) {
                const importedItems = Array.isArray(result) ? result : validated;
                toast.success(`Imported ${validated.length} records. Uploaded ${uploadedCount.value} Mini SDS PDFs${softDeletedCount.value ? ` and soft-deleted ${softDeletedCount.value} incomplete record(s).` : "."}`);
                await persistChemicals(importedItems);
            }
        } catch (error) {
            console.error(error);
            const friendlyMessage = error?.message || "The ZIP file is not in the expected format. Please upload a ZIP containing an HTML file with a chemicalData array and the related PDF documents.";
            setImportError(friendlyMessage);
            toast.error(friendlyMessage);
        } finally {
            setImporting(false);
            event.target.value = "";
        }
    };

    const visibleItems = sortChemicals(showUnpublished ? items : items.filter((chemical) => !((chemical?.IsDeleted ?? chemical?.isDeleted) ?? false)));

    return (
        <Container className="mt--7" fluid>
            <Row>
                <Col className="mb-5 mb-xl-0" xl="12">
                    <Card className="shadow">
                        <CardHeader className="bg-transparent">
                            <Row className="align-items-center justify-content-between">
                                <Col>
                                    <h3 className="mb-0">Administration &gt; Chemicals</h3>
                                </Col>
                                <Col className="text-right">
                                    <label className="custom-control custom-checkbox d-inline-flex align-items-center mr-3">
                                        <Input
                                            type="checkbox"
                                            className="custom-control-input"
                                            checked={showUnpublished}
                                            onChange={(event) => setShowUnpublished(event.target.checked)}
                                        />
                                        <span className="custom-control-label">Show unpublished chemicals</span>
                                    </label>
                                    <button className="btn btn-secondary mr-2" onClick={load}>Reload</button>
                                    <label className="btn btn-outline-primary mb-0 mr-2">
                                        {isImporting ? "Importing..." : "Import ZIP"}
                                        <input
                                            type="file"
                                            accept=".zip,application/zip"
                                            hidden
                                            onChange={importZip}
                                        />
                                    </label>
                                </Col>
                            </Row>
                        </CardHeader>
                        <CardBody>
                            <p className="text-muted">Upload the sample ZIP archive to extract the chemical JSON array from the embedded HTML and upload the matching Mini SDS PDFs directly to blob storage.</p>
                            {importError && (
                                <div className="alert alert-danger mb-3" role="alert">
                                    {importError}
                                </div>
                            )}
                            <div className="table-responsive">
                                <table className="table align-items-center table-flush">
                                    <thead className="thead-light">
                                        <tr>
                                            <th scope="col">Document Name</th>
                                            <th scope="col">Vendor</th>
                                            <th scope="col">Issue Date</th>
                                            <th scope="col">Document Number</th>
                                            <th scope="col">Status</th>
                                            <th scope="col">Mini SDS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleItems.map((chemical, index) => (
                                            <tr key={`${chemical.DocumentName || chemical.documentName || 'chemical'}-${index}`}>
                                                <td>{chemical.DocumentName || chemical.documentName || "-"}</td>
                                                <td>{chemical.Vendor || chemical.vendor || "-"}</td>
                                                <td>{formatIssueDate(chemical.IssueDate || chemical.issueDate)}</td>
                                                <td>{chemical.DocumentNumber || chemical.documentNumber || "-"}</td>
                                                <td>
                                                    <span className={`badge ${((chemical?.IsDeleted ?? chemical?.isDeleted) ?? false) ? "badge-danger" : "badge-success"}`}>
                                                        {((chemical?.IsDeleted ?? chemical?.isDeleted) ?? false) ? "Unpublished" : "Published"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Button color="primary" size="sm" onClick={() => downloadMiniSds(chemical)}>Download Mini SDS</Button>
                                                        <Button
                                                            color={((chemical?.IsDeleted ?? chemical?.isDeleted) ?? false) ? "success" : "secondary"}
                                                            size="sm"
                                                            className="ml-2"
                                                            onClick={() => toggleChemicalVisibility(chemical)}
                                                        >
                                                            {((chemical?.IsDeleted ?? chemical?.isDeleted) ?? false) ? "Publish" : "Unpublish"}
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-3 text-muted">Showing {visibleItems.length} of {items.length} records</div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};
